import { inject, Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    RouterStateSnapshot,
} from '@angular/router';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import { UserService } from '@services/api/user/user.service';
import { RouterService } from '@services/router.service';
import { FeatureFlagService } from '@services/feature-flag.service';
import { RoleRoutePermissions, Route } from '@constants/route';
import { HttpErrorResponse } from '@angular/common/http';
import { Role } from '@models/user';

@Injectable({
    providedIn: 'root',
})
export class RoleGuard implements CanActivate {
    private readonly userService = inject(UserService);
    private readonly routerService = inject(RouterService);
    private readonly featureFlagService = inject(FeatureFlagService);

    canActivate(
        _route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean> {
        const featureFlags$ = this.featureFlagService.hasLoaded()
            ? of(null)
            : this.featureFlagService.getFeatureFlags();

        return featureFlags$.pipe(
            switchMap(() => this.userService.getUser()),
            map((user) => {
                if (user) {
                    try {
                        const accountAdminGrantedAccessToConfigurations =
                            this.featureFlagService.getFeature(
                                'AccountAdminGrantedAccessToConfigurations',
                            );
                        const role = user.role;
                        const route = state.url.substring(1) as Route;
                        let permittedRoutes =
                            RoleRoutePermissions[role!].permittedRoutes;

                        if (
                            role === Role.Admin &&
                            !accountAdminGrantedAccessToConfigurations
                        ) {
                            permittedRoutes = permittedRoutes.filter(
                                (route) => route !== Route.Configuration,
                            );
                        }
                        const permitted = isRoutePermitted(
                            route,
                            permittedRoutes,
                        );

                        if (permitted) return true;
                        else {
                            this.routerService.goApp();
                            return false;
                        }
                    } catch {
                        this.routerService.goApp();
                        return false;
                    }
                }
                this.routerService.goApp();
                return false;
            }),
            catchError((err: HttpErrorResponse) => {
                this.routerService.goErrorPage(err);
                return of(false);
            }),
        );
    }
}

function isRoutePermitted(route: string, permittedRoutes: string[]): boolean {
    return permittedRoutes.some((pattern) => {
        const regex = convertToRegex(pattern);
        return regex.test(route);
    });
}

function convertToRegex(route: string): RegExp {
    const regexStr = '^' + route.replace(/:[^/]+/g, '[^/]+' + '$');
    return new RegExp(regexStr);
}
